import {useUpmPlant} from '@hconnect/upm'
import {Button} from '@hm/fundamental'
import {mdiWindowClose, mdiCheck} from '@mdi/js'
import {useFormContext, type UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useKilns} from '@refra/modules/common/providers'
import type {ShutdownForm} from '@refra/modules/common/types'
import {useEditShutdown} from '@refra/modules/refra-planning/hooks/mutations'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface EditKilnShutdownActionsProps {
  setIsEditMode: (isEditMode: boolean) => void
}

export const EditKilnShutdownActions: React.FC<EditKilnShutdownActionsProps> = ({
  setIsEditMode
}) => {
  const {t} = useTranslation()

  const plantCode = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {selectedKiln} = useKilns()

  const {mutate: editShutdown, isPending: isEditInProgress} = useEditShutdown()

  const {
    formState: {isDirty, isValid},
    handleSubmit,
    reset
  }: UseFormReturn<ShutdownForm> = useFormContext()

  const onCancelKilnShutdownChanges = () => {
    reset()
    setIsEditMode(false)
  }

  const onSaveKilnShutdownChanges = handleSubmit((kilnShutdownForm) => {
    if (!isDirty || !isValid) return

    const {id, shutdownType, name, interventionDate, feedingDate} = kilnShutdownForm

    return editShutdown(
      {
        plantUpmId: upmPlant.upmId,
        kilnUpmId: selectedKiln.upmId,
        shutdownId: id as number,
        shutdownDTO: {name, shutdownType, interventionDate, feedingDate}
      },
      {
        onSuccess: () => {
          reset(kilnShutdownForm)
          setIsEditMode(false)
        }
      }
    )
  })

  return (
    <div className="flex gap-3">
      <Button
        icon={mdiWindowClose}
        disabled={isEditInProgress}
        variant="secondary"
        onClick={onCancelKilnShutdownChanges}
        data-cy="kiln_shutdown_card_cancel_shutdown_edit_button"
      >
        {t('common.cancel')}
      </Button>
      <Button
        icon={mdiCheck}
        disabled={!isDirty || isEditInProgress}
        onClick={onSaveKilnShutdownChanges}
        data-cy="kiln_shutdown_card_save_shutdown_edit_button"
      >
        {t('common.done')}
      </Button>
    </div>
  )
}
